import React, { useState, useRef, useEffect, useContext } from "react";
import { useTheme } from "@material-ui/core/styles";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
// import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import defaultLogoFavicon from "../../assets/fluyra-favicon.png";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const shouldIgnoreAuthError = (err) => {
	const status = err?.response?.status;
	return status === 401 || status === 403;
};

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
}));

const NotificationsPopOver = (volume) => {
	const classes = useStyles();
	const theme = useTheme();

	const history = useHistory();
	// const socketManager = useContext(SocketContext);
	const { user, socket, isAuth, loading: authLoading } = useContext(AuthContext);
	const { queues } = user;

	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [browserTicketIds, setBrowserTicketIds] = useState([]);
	const { get: getSetting } = useCompanySettings();
    const { setTabOpen } = useContext(TicketsContext);

	const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
	const [showNotificationPending, setShowNotificationPending] = useState(false);
	const [showGroupNotification, setShowGroupNotification] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({
		withUnreadMessages: "true"
		// showAll: showTicketWithoutQueue ? "true" : "false"
	});

	const [play] = useSound(alertSound, volume);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	const addBrowserTicket = (ticketId) => {
		setBrowserTicketIds(prevState => {
			if (prevState.includes(ticketId)) {
				return prevState;
			}
			return [ticketId, ...prevState];
		});
	};

	const removeBrowserTicket = (ticketId) => {
		setBrowserTicketIds(prevState => prevState.filter(id => id !== ticketId));
	};

	useEffect(() => {
		if (authLoading) {
			return;
		}

		if (!isAuth || !user?.companyId) {
			setShowTicketWithoutQueue(false);
			setShowNotificationPending(false);
			setShowGroupNotification(false);
			return;
		}

		let isMounted = true;

		const fetchSettings = async () => {
			try {
				const setting = await getSetting(
					{
						"column": "showNotificationPending"
					}
				);



				if (!isMounted) {
					return;
				}

				if (setting.showNotificationPending === true) {
					setShowNotificationPending(true);
				} else {
					setShowNotificationPending(false);
				}

				setShowTicketWithoutQueue(user.allTicket === "enable");
				setShowGroupNotification(user.allowGroup === true);
			} catch (err) {
				if (!shouldIgnoreAuthError(err)) {
					toastError(err);
				}
			}
		}

		fetchSettings();
		return () => {
			isMounted = false;
		};
	}, [authLoading, getSetting, isAuth, user?.allTicket, user?.allowGroup, user?.companyId]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		const processNotifications = () => {
			setNotifications(tickets);
			setBrowserTicketIds(prevState => {
				const unreadTicketIds = tickets.map(ticket => ticket.id);
				return [...new Set([...prevState, ...unreadTicketIds])];
			});
		}

		processNotifications();
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	const shouldTrackTicket = (ticket) => {
		return (ticket?.userId === user?.id || !ticket?.userId) &&
			(user?.queues?.some(queue => (queue.id === ticket?.queueId)) ||
				(!ticket?.queueId && showTicketWithoutQueue === true)) &&
			(!["pending", "lgpd", "nps", "group"].includes(ticket?.status) ||
				(ticket?.status === "pending" && showNotificationPending === true) ||
				(ticket?.status === "group" && ticket?.whatsapp?.groupAsTicket === "enabled" && showGroupNotification === true));
	};

	const shouldNotifyNewTicket = (ticket) => {
		if (!ticket) {
			return false;
		}

		return !["closed", "lgpd", "nps"].includes(ticket.status);
	};

	const normalizeTicketSocketEvent = (data) => {
		if (data?.ticket) {
			return {
				action: data.action,
				ticket: data.ticket,
				ticketId: data.ticketId || data.ticket?.id,
			};
		}

		if (data?.id && data?.uuid) {
			return {
				action: "update",
				ticket: data,
				ticketId: data.id,
			};
		}

		return {
			action: data?.action,
			ticket: null,
			ticketId: data?.ticketId,
		};
	};

	useEffect(() => {
		const browserCount = browserTicketIds.length;
		if (browserCount > 0) {
			document.title = "(" + browserCount + ") " + (theme.appName || "...");
		} else {
			document.title = theme.appName || "...";
		}
	}, [browserTicketIds.length, theme.appName]);

	useEffect(() => {
		const faviconHref = theme?.appLogoFavicon || defaultLogoFavicon;
		const iconLink =
			document.querySelector("link[rel='icon']") ||
			document.querySelector("link[rel='shortcut icon']");
		const browserCount = browserTicketIds.length;
		if (!iconLink) {
			return;
		}

		if (!browserCount) {
			iconLink.setAttribute("href", faviconHref);
			return;
		}

		const image = new Image();
		image.crossOrigin = "anonymous";
		image.onload = () => {
			try {
				const canvas = document.createElement("canvas");
				canvas.width = 64;
				canvas.height = 64;
				const context = canvas.getContext("2d");

				if (!context) {
					iconLink.setAttribute("href", faviconHref);
					return;
				}

				context.clearRect(0, 0, 64, 64);
				context.drawImage(image, 0, 0, 64, 64);
				context.fillStyle = "#ef4444";
				context.beginPath();
				context.arc(48, 16, 14, 0, Math.PI * 2);
				context.fill();
				context.fillStyle = "#ffffff";
				context.font = "bold 16px Arial";
				context.textAlign = "center";
				context.textBaseline = "middle";
				context.fillText(browserCount > 99 ? "99" : String(browserCount), 48, 16);

				iconLink.setAttribute("href", canvas.toDataURL("image/png"));
			} catch (error) {
				console.error("[NotificationsPopOver] favicon badge render failed", error);
				iconLink.setAttribute("href", faviconHref);
			}
		};
		image.onerror = () => {
			iconLink.setAttribute("href", faviconHref);
		};
		image.src = faviconHref;
	}, [browserTicketIds.length, theme?.appLogoFavicon]);

	useEffect(() => {
		const companyId = user.companyId;
		// const socket = socketManager.GetSocket();
		if (user.id && isAuth && socket?.on && socket?.off) {

			const onConnectNotificationsPopover = () => {
				socket.emit("joinNotification");
			}

			const onCompanyTicketNotificationsPopover = (data) => {
				const normalizedEvent = normalizeTicketSocketEvent(data);
				const eventAction = normalizedEvent.action;
				const eventTicket = normalizedEvent.ticket;
				const eventTicketId = normalizedEvent.ticketId;

				if (eventAction === "updateUnread" || eventAction === "delete") {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === eventTicketId);
						if (ticketIndex !== -1) {
							prevState.splice(ticketIndex, 1);
							return [...prevState];
						}
						return prevState;
					});
					removeBrowserTicket(eventTicketId);

					setDesktopNotifications(prevState => {
						const notfiticationIndex = prevState.findIndex(
							n => n.tag === String(eventTicketId)
						);
						if (notfiticationIndex !== -1) {
							prevState[notfiticationIndex].close();
							prevState.splice(notfiticationIndex, 1);
							return [...prevState];
						}
						return prevState;
					});
				}

				if ((eventAction === "create" || eventAction === "update") && eventTicket) {
					if (shouldTrackTicket(eventTicket)) {
						setNotifications(prevState => {
							const ticketIndex = prevState.findIndex(t => t.id === eventTicket.id);
							if (ticketIndex !== -1) {
								prevState[ticketIndex] = eventTicket;
								return [...prevState];
							}
							return [eventTicket, ...prevState];
						});
					}

					if (shouldNotifyNewTicket(eventTicket)) {
						addBrowserTicket(eventTicket.id);
					}
				}
			};

			const onCompanyAppMessageNotificationsPopover = (data) => {
				if (data?.action === "create" && data?.ticket?.id && !data?.message?.fromMe) {
					addBrowserTicket(data.ticket.id);
				}
				
				if (
					data.action === "create" && !data.message.fromMe &&
					!data.message.read &&
					shouldTrackTicket(data.ticket)
				) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
						if (ticketIndex !== -1) {
							prevState[ticketIndex] = data.ticket;
							return [...prevState];
						}
						return [data.ticket, ...prevState];
					});

					const shouldNotNotificate =
						(data.message.ticketId === ticketIdRef.current &&
							document.visibilityState === "visible") ||
						(data.ticket.userId && data.ticket.userId !== user?.id) ||
						(data.ticket.isGroup && data.ticket?.whatsapp?.groupAsTicket === "disabled" && showGroupNotification === false);


					if (shouldNotNotificate === true) return;

					handleNotifications(data);
				}
			}

			socket.on("connect", onConnectNotificationsPopover);
			socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
			socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);

			return () => {
				socket.off("connect", onConnectNotificationsPopover);
				socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
				socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
			};
		}
	}, [isAuth, queues, showGroupNotification, showNotificationPending, showTicketWithoutQueue, socket, user]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.urlPicture,
			tag: ticket.id,
			renotify: true,
		};
		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			setTabOpen(ticket.status)
			historyRef.current.push(`/tickets/${ticket.uuid}`);
			// handleChangeTab(null, ticket.isGroup? "group" : "open");
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
		soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
				style={{ color: "white" }}
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;


